import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { sendVoiceCommand } from '../../services/voiceService';
import { TablerMic } from '../shared/Icons';
import './VoiceCommandPanel.css';

const VoiceCommandPanel = ({ onResponse, showTitle = true }) => {
  const { t } = useTranslation();
  const [transcript, setTranscript] = useState('');
  const [status, setStatus] = useState(t("status.sayHelloApplied"));
  const [isListening, setIsListening] = useState(false);
  const WAKE_WORD = 'hello applied';
  const isListeningRef = useRef(false);
  const recognitionRef = useRef(null);
  const isRunningRef = useRef(false);
  const shouldRunRef = useRef(true);

  const onResponseRef = useRef(onResponse);
  onResponseRef.current = onResponse;

  const handleCommand = useCallback(async (command) => {
    setTranscript(command);
    setStatus(t("status.processingCommand"));
    isListeningRef.current = true;

    try {
      const result = await sendVoiceCommand(command);
      if (result) {
        setStatus(t("status.commandProcessed"));
        if (Array.isArray(result) && result.length > 0) {
          if (onResponseRef.current) {
            onResponseRef.current({
              command: `${WAKE_WORD} ${command}`,
              alerts: result
            });
          }
        }
        if (Array.isArray(result) && result.length === 0) {
          setStatus(t("status.noAlertsFound", { command }));
        }
      } else {
        setStatus(t("status.failedProcessCommand"));
      }
    } catch (err) {
      console.error('Command error:', err);
      setStatus(t("status.errorSendingCommand"));
    }
  }, [t]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setStatus(t("status.speechNotSupported"));
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      isRunningRef.current = true;
      console.log('Speech recognition started');
      if (isListeningRef.current) {
        setStatus(t("status.listeningEllipsis"));
      } else {
        setStatus(t("status.sayHelloApplied"));
      }
    };

    recognition.onresult = (event) => {
      const heard = event.results[event.results.length - 1][0].transcript.toLowerCase().trim();
      console.log('Heard:', heard);

      let command = '';
      if (heard.includes(WAKE_WORD)) {
        setStatus(t("status.wakeWordDetected"));
        isListeningRef.current = true;
        setIsListening(true);
        command = heard.split(WAKE_WORD).pop().trim();
      } else if (isListeningRef.current) {
        command = heard;
      }

      if (command) {
        setTranscript(command);
        setStatus(t("status.heardCommand"));
        handleCommand(command);
      }
    };

    recognition.onerror = (e) => {
      console.error('Recognition error:', e);
      isRunningRef.current = false;
      if (e.error === 'not-allowed') {
        setStatus(t("status.micBlocked"));
      } else {
        setStatus(t("status.errorPrefix", { detail: e.error }));
      }
      setIsListening(false);
      isListeningRef.current = false;
    };

    recognition.onend = () => {
      isRunningRef.current = false;
      console.log('Recognition ended.');
      if (shouldRunRef.current) {
        try {
          recognition.start();
        } catch (err) {
          console.warn('Restart failed:', err);
        }
      }
    };

    shouldRunRef.current = true;
    try {
      recognition.start();
    } catch (err) {
      console.warn('Initial start failed:', err);
    }

    return () => {
      shouldRunRef.current = false;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch (e) {}
    };
  }, [handleCommand, t]);

  const startListeningService = () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    shouldRunRef.current = true;
    isListeningRef.current = true;
    setIsListening(true);

    if (!isRunningRef.current) {
      try {
        recognition.start();
        setStatus('Listening...');
      } catch (err) {
        console.warn('Start failed:', err);
        setStatus(t("status.unableToStartRecognition"));
      }
    } else {
      setStatus(t("status.listeningEllipsis"));
    }
  };

  const stopListeningService = () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    shouldRunRef.current = false;
    isListeningRef.current = false;
    setIsListening(false);
    try {
      recognition.stop();
    } catch (e) {}
    setStatus(t("status.notListening"));
    setTranscript('');
  };

  const handleToggleListen = () => {
    if (isListening) {
      stopListeningService();
    } else {
      startListeningService();
    }
  };

  return (
    <div className="voice-panel">
      {showTitle && <h2 className="voice-title">{t("voice.voiceAlerts")}</h2>}
      <p className="voice-instruction">
        {t("voice.handsFreeInstruction")}
      </p>

      {/* Circle Mic Button Wrapper */}
      <div className="voice-mic-wrapper">
        <div 
          className={`voice-mic-outer-ring ${isListening ? 'listening' : 'idle'}`}
          onClick={handleToggleListen}
        >
          <div className={`voice-mic-inner-circle ${isListening ? 'listening' : 'idle'}`}>
            <TablerMic color="#ffffff" size={40} />
          </div>
        </div>
      </div>

      {/* Listening Status Indicators */}
      <div className="voice-status-container">
        <div className="voice-status-header">
          <span className={`voice-status-dot ${isListening ? 'listening' : 'idle'}`}>●</span>
          <span className="voice-status-label">
            {status}
          </span>
        </div>
        <p className="voice-status-subtext">
          <Trans i18nKey="voice.tapOrSayInstruction" values={{ wakeWord: WAKE_WORD }}>
            Tap the mic, or say <span className="voice-wake-word-highlight">{'"hello applied"'}</span> to activate.
          </Trans>
        </p>
      </div>

      {/* ReadOnly Transcript Box if active */}
      {isListening && transcript && (
        <div className="voice-transcript-wrapper">
          <input
            type="text"
            value={transcript}
            placeholder={t("placeholder.speakCommand")}
            readOnly
            className="voice-transcript-input"
          />
        </div>
      )}

      {/* Toggle Button */}
      <div className="voice-action-btn-wrapper">
        <button 
          onClick={handleToggleListen} 
          className={`voice-action-btn ${isListening ? 'listening' : 'idle'}`}
        >
          {isListening ? t("button.stopListening") : t("button.tapToListen")}
        </button>
      </div>

      {/* Footer Info */}
      <p className="voice-footer-text">
        {t("voice.micFooter")}
      </p>
    </div>
  );
};

export default VoiceCommandPanel;
